/**
 * FmMmlBasemapTiles / FmStandBasemapTiles — the canonical MML basemaps for all
 * Leaflet maps.
 *
 * Two MML Karttakuvapalvelu layers, served through the FastAPI proxy
 * `/api/v1/mml/tiles/{layer}/{z}/{x}/{y}.png` which injects the MML api-key
 * server-side:
 *
 * - `maastokartta` — the officially-navigable Finnish topographic base.
 * - `ortokuva`    — the national orthophoto program (0.5 m GSD, whole country
 *                   reflown on a ~3-year cycle, CC BY 4.0). This is the
 *                   satellite/aerial layer; do NOT reintroduce Esri
 *                   World_Imagery (license grey zone, unknown capture dates).
 *
 * If the proxy reports itself unconfigured, or a tile fails before the
 * capability probe has answered, the layer uses the Leaflet-standard
 * OpenStreetMap base so the map is always readable. A tile that fails while
 * the proxy reports itself configured (the proxy's 429 budget, an upstream
 * timeout) keeps the MML layer (`resolveBasemapMode`): the failed tile is
 * requested again once the proxy's admission window has passed, and while
 * any viewport area stays uncovered the gap is reported to the map's owner,
 * or shown as a map notice when no owner listens. Direct
 * tile.openstreetmap.org use is allowed ONLY on the fallback path —
 * production-volume primary use violates the OSMF tile usage policy.
 *
 * Ortokuva generalization and OSM-primary basemap tiles.
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import { LayerGroup, TileLayer, useMap, useMapEvents } from 'react-leaflet';

import {
  resolveBasemapMode,
  useMmlTilesAvailable,
} from '@/shared/config/mmlTileAvailability';
import {
  BASEMAP_NATIVE_MAX_ZOOM,
  type BasemapTone,
} from '@/shared/config/basemapCoverage';
import { FmMapRasterNotice } from '@/components/ui/FmMapRasterNotice';
import { useLanguage } from '@/i18n';
import { useDeclareMapGround } from '@/components/Map/mapGround';
import './FmStandBasemapTiles.css';

export type MmlBasemapLayer = 'maastokartta' | 'ortokuva';

const MML_TILE_URL: Record<MmlBasemapLayer, string> = {
  maastokartta: '/api/v1/mml/tiles/maastokartta/{z}/{x}/{y}.png',
  ortokuva: '/api/v1/mml/tiles/ortokuva/{z}/{x}/{y}.png',
};
const MML_ATTRIBUTION = '&copy; Maanmittauslaitos';
const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/**
 * Keep the visible raster stable while trackpads emit several fractional zoom
 * steps. Updating the grid at every intermediate integer zoom cancels pending
 * tiles and can expose the map canvas before the replacement grid is ready.
 * A wider buffer also keeps nearby tiles mounted during short pans.
 */
const STABLE_TILE_GRID_PROPS = {
  keepBuffer: 3,
  updateWhenZooming: false,
} as const;
const CONTINUITY_MAX_NATIVE_ZOOM = 14;
/**
 * The gateway admits upstream MML fetches per client in a 60 s window
 * (`MmlTileRateLimiter.window_seconds`) and answers a refused tile with a
 * Retry-After of at most that window. Leaflet never re-requests a failed tile,
 * so a configured-proxy failure is requested again after the window has
 * certainly passed; two attempts cover a transient refusal without turning a
 * persistent upstream outage into a request loop.
 */
const TILE_RETRY_DELAY_MS = 61_000;
const MAX_TILE_RETRIES = 2;

interface StableRasterTileGridProps {
  url: string;
  attribution: string;
  maxZoom: number;
  /** Deepest level the provider publishes; deeper zooms resample it. */
  maxNativeZoom: number;
  tone: BasemapTone;
  onTileError?: () => void;
  onRasterUnavailableChange?: (unavailable: boolean) => void;
  /** Request failed tiles again after the proxy's admission window. */
  retryFailedTiles?: boolean;
}

/** Remove a loaded tile's coverage from a visible gap, in zoom-zero pixels. */
function withoutRasterTile(gap: L.Bounds, tile: L.Bounds): L.Bounds[] {
  if (!gap.overlaps(tile)) return [gap];
  const left = Math.max(gap.min!.x, tile.min!.x);
  const right = Math.min(gap.max!.x, tile.max!.x);
  const top = Math.max(gap.min!.y, tile.min!.y);
  const bottom = Math.min(gap.max!.y, tile.max!.y);
  return [
    L.bounds([gap.min!.x, gap.min!.y], [gap.max!.x, top]),
    L.bounds([gap.min!.x, bottom], [gap.max!.x, gap.max!.y]),
    L.bounds([gap.min!.x, top], [left, bottom]),
    L.bounds([right, top], [gap.max!.x, bottom]),
  ].filter((part) => part.getSize().x > 0 && part.getSize().y > 0);
}

/**
 * Keep a coarse tile grid beneath the active detail grid. Leaflet can discard
 * the prior zoom level before a cold replacement batch finishes, especially
 * when a trackpad crosses several levels quickly. The continuity grid stays at
 * zoom 14 (and is therefore reusable in the browser cache) while the
 * detailed grid loads the requested level above it.
 */
const StableRasterTileGrid: React.FC<StableRasterTileGridProps> = ({
  url,
  attribution,
  maxZoom,
  maxNativeZoom,
  tone,
  onTileError,
  onRasterUnavailableChange,
  retryFailedTiles = false,
}) => {
  const map = useMap();
  // Only the detail grid carries `fm-basemap-tiles`: fits read their zoom cap
  // from it, and the coarse continuity grid would cap them at z14.
  const muted = tone === 'muted' ? ' fm-basemap--muted' : '';
  const detailClassName = `fm-basemap-tiles${muted}`;
  const continuityClassName = `fm-basemap-continuity${muted}`;
  // Element identity separates both layers and rejects late, unloaded results.
  // The keyed grid remount discards the entire set when the provider changes.
  const tiles = React.useRef(new Map<HTMLElement, {
    bounds: L.Bounds; status: 'pending' | 'loaded' | 'failed';
  }>());
  const reportCoverage = React.useCallback(() => {
    const viewport = map.getBounds();
    let gaps = [L.bounds(map.project(viewport.getNorthWest(), 0), map.project(viewport.getSouthEast(), 0))];
    const results = [...tiles.current.values()];
    for (const tile of results) {
      if (tile.status === 'loaded') gaps = gaps.flatMap((gap) => withoutRasterTile(gap, tile.bounds));
    }
    if (gaps.length === 0) onRasterUnavailableChange?.(false);
    else if (results.some((tile) => tile.status === 'failed' && gaps.some((gap) => gap.overlaps(tile.bounds)))) {
      onRasterUnavailableChange?.(true);
    }
    // Pending imagery is not recovery evidence: retain the owner's last state.
  }, [map, onRasterUnavailableChange]);
  useMapEvents({ moveend: reportCoverage, resize: reportCoverage });
  const retries = React.useRef(new Map<HTMLElement, { attempts: number; timer?: number }>());
  React.useEffect(() => {
    const pending = retries.current;
    return () => {
      for (const retry of pending.values()) window.clearTimeout(retry.timer);
      pending.clear();
    };
  }, []);
  const scheduleRetry = React.useCallback((tile: HTMLElement) => {
    const src = tile.getAttribute('src');
    const retry = retries.current.get(tile) ?? { attempts: 0 };
    if (!src || retry.attempts >= MAX_TILE_RETRIES) return;
    retry.attempts += 1;
    retry.timer = window.setTimeout(() => {
      const result = tiles.current.get(tile);
      if (!result) return;
      result.status = 'pending';
      // Re-setting the attribute re-runs the image load; Leaflet's own load
      // listener then fires `tileload` for the same tile element.
      tile.setAttribute('src', src);
    }, TILE_RETRY_DELAY_MS);
    retries.current.set(tile, retry);
  }, []);
  const eventHandlers = React.useMemo(() => ({
    tileloadstart: ({ tile, coords }: L.TileEvent) => {
      const size = 256 / 2 ** coords.z;
      // A fitted map sits at a fractional zoom, where each scaled tile edge is
      // rasterised anti-aliased and the canvas shows through as hairline seams
      // (Leaflet issue #3575). One pixel of overlap closes them; the 1/256
      // stretch is below what the eye reads.
      tile.style.width = `${(Number.parseFloat(tile.style.width) || 256) + 1}px`;
      tile.style.height = `${(Number.parseFloat(tile.style.height) || 256) + 1}px`;
      tiles.current.set(tile, {
        bounds: L.bounds([coords.x * size, coords.y * size], [(coords.x + 1) * size, (coords.y + 1) * size]),
        status: 'pending',
      });
    },
    tileerror: ({ tile }: L.TileErrorEvent) => {
      const result = tiles.current.get(tile);
      if (!result) return;
      result.status = 'failed';
      onTileError?.();
      if (retryFailedTiles) scheduleRetry(tile);
      reportCoverage();
    },
    tileload: ({ tile }: L.TileEvent) => {
      const result = tiles.current.get(tile);
      if (!result) return;
      result.status = 'loaded';
      reportCoverage();
    },
    tileunload: ({ tile }: L.TileEvent) => {
      tiles.current.delete(tile);
      window.clearTimeout(retries.current.get(tile)?.timer);
      retries.current.delete(tile);
      reportCoverage();
    },
  }), [onTileError, reportCoverage, retryFailedTiles, scheduleRetry]);

  return (
    <>
      <TileLayer
        {...STABLE_TILE_GRID_PROPS}
        key={`${url}:${tone}:continuity`}
        url={url}
        attribution={attribution}
        maxNativeZoom={Math.min(CONTINUITY_MAX_NATIVE_ZOOM, maxNativeZoom)}
        maxZoom={maxZoom}
        className={continuityClassName}
        zIndex={0}
        eventHandlers={eventHandlers}
      />
      <TileLayer
        {...STABLE_TILE_GRID_PROPS}
        key={`${url}:${tone}:detail`}
        url={url}
        attribution={attribution}
        maxNativeZoom={maxNativeZoom}
        maxZoom={maxZoom}
        className={detailClassName}
        zIndex={1}
        eventHandlers={eventHandlers}
      />
    </>
  );
};

export interface FmMmlBasemapTilesProps {
  /** Which MML raster layer to render. Defaults to the topographic base. */
  layer?: MmlBasemapLayer;
  /**
   * Terminal raster gaps and their recovery, for the map's owning state UI.
   * Without a listener the basemap shows its own map notice.
   */
  onRasterUnavailableChange?: (unavailable: boolean) => void;
  /** `muted` quiets the basemap under dense overview data. */
  tone?: BasemapTone;
}

export const FmMmlBasemapTiles: React.FC<FmMmlBasemapTilesProps> = ({
  layer = 'maastokartta',
  onRasterUnavailableChange,
  tone = 'standard',
}) => {
  const [useFallback, setUseFallback] = useState(false);
  // Capability discovery: when the proxy reports MML rasters unconfigured,
  // go straight to OSM instead of probing with guaranteed-503 tile requests
  // (each of which logs a browser console error before tileerror fires).
  // While the check is still in flight (null) we render MML optimistically —
  // never a blank basemap — protected by the tileerror→OSM fallback below;
  // see resolveBasemapMode for why keyless CI still avoids the probe burst.
  const mmlAvailable = useMmlTilesAvailable();
  const map = useMap();
  const { t } = useLanguage();
  const [rasterGap, setRasterGap] = useState(false);
  const reportCoverage = React.useCallback((unavailable: boolean) => {
    setRasterGap(unavailable);
    onRasterUnavailableChange?.(unavailable);
  }, [onRasterUnavailableChange]);
  // A failed MML tile is terminal only while the proxy reports itself
  // configured; otherwise the OSM base replaces the layer and owns the state.
  const reportMmlCoverage = React.useCallback((unavailable: boolean) => {
    if (unavailable && mmlAvailable !== true) return;
    reportCoverage(unavailable);
  }, [mmlAvailable, reportCoverage]);
  const enableFallback = React.useCallback(() => setUseFallback(true), []);
  const mode = resolveBasemapMode(mmlAvailable, useFallback);
  useDeclareMapGround(mode !== 'osm' && layer === 'ortokuva' ? 'imagery' : 'paper');

  const raster =
    mode === 'osm' ? (
      <StableRasterTileGrid
        key="osm"
        url={OSM_TILE_URL}
        attribution={OSM_ATTRIBUTION}
        maxZoom={19}
        maxNativeZoom={BASEMAP_NATIVE_MAX_ZOOM.osm}
        tone={tone}
        onRasterUnavailableChange={reportCoverage}
      />
    ) : (
      <StableRasterTileGrid
        key={layer}
        url={MML_TILE_URL[layer]}
        attribution={MML_ATTRIBUTION}
        maxZoom={18}
        maxNativeZoom={BASEMAP_NATIVE_MAX_ZOOM[layer]}
        tone={tone}
        // A failed MML tile switches to OSM only while the proxy's
        // configuration is unknown; resolveBasemapMode owns the rule.
        onTileError={enableFallback}
        onRasterUnavailableChange={reportMmlCoverage}
        retryFailedTiles={mmlAvailable === true}
      />
    );
  const notice =
    rasterGap && !onRasterUnavailableChange
      ? createPortal(
          <FmMapRasterNotice
            message={t.shell.basemap.tilesUnavailable}
            testId="fm-basemap-gap-notice"
          />,
          map.getContainer(),
        )
      : null;

  return (
    <LayerGroup>
      {raster}
      {notice}
    </LayerGroup>
  );
};

/** Canonical raster basemap for DS stand maps. */
export const FmStandBasemapTiles: React.FC<FmMmlBasemapTilesProps> = ({
  layer = 'maastokartta',
  onRasterUnavailableChange,
  tone,
}) => (
  <FmMmlBasemapTiles
    layer={layer}
    onRasterUnavailableChange={onRasterUnavailableChange}
    tone={tone}
  />
);

export default FmStandBasemapTiles;
